
import { useCallback } from "react";
import { Movie } from "@/types/movie";
import { SingleListService } from "@/services/movie/singleListService";
import { SINGLE_LIST_TYPE } from "@/hooks/search/types/listConstants";
import { isDebugMovie } from "@/utils/debugLogger";
import { fetchMovieDetails } from "@/services/movieService";
import { useAuth } from "@/context/AuthContext";

/**
 * Hook for committing plan to watch status changes with immediate local updates
 */
export const usePlanToWatchCommit = (
  allMovies: Movie[],
  addMovie: (movie: Movie) => void,
  updateMovieState: (tmdbId: number, props: Partial<Movie>) => void,
  getVisualState: (tmdbId: number, propertyName: 'favorite' | 'planToWatch') => { isPending: boolean; isOptimistic: boolean; currentValue: boolean }
) => {
  const { user } = useAuth();
  
  const commitTogglePlanToWatch = useCallback(async (movie: Movie) => {
    if (!movie.tmdbId) {
      console.error("❌ Cannot commit plan to watch toggle: Missing tmdbId", movie);
      return;
    }

    const shouldLog = isDebugMovie(movie) || true;
    
    // Use current visual state for toggle logic
    const currentPlanToWatchState = getVisualState(movie.tmdbId, 'planToWatch');
    const newPlanToWatchValue = !currentPlanToWatchState.currentValue;
    
    console.log(`🔍 [COMMIT-WATCH] Starting watchlist toggle with immediate local update:`, {
      title: movie.title,
      tmdbId: movie.tmdbId,
      currentVisualPlanToWatch: currentPlanToWatchState.currentValue,
      newPlanToWatchValue
    });

    // Check if we need complete metadata
    const existingMovie = allMovies.find(m => m.tmdbId === movie.tmdbId);
    let completeMovie = movie;

    // Always update local state FIRST for immediate UI response
    if (existingMovie) {
      // Update existing movie immediately
      updateMovieState(movie.tmdbId, { 
        ...existingMovie, 
        planToWatch: newPlanToWatchValue 
      });
      console.log(`✅ [COMMIT-WATCH] Immediate local update for existing movie: ${movie.title}`);
    } else {
      // Add new movie to collection immediately
      completeMovie = {
        ...movie,
        favorite: movie.favorite ?? false,
        planToWatch: newPlanToWatchValue
      };
      addMovie(completeMovie);
      console.log(`✅ [COMMIT-WATCH] Immediate local addition of new movie: ${movie.title}`);
    }

    // Handle database persistence in background
    try {
      // Fetch complete metadata if needed (background operation)
      const needsMetadata = !existingMovie || 
                           !movie.runtime || 
                           !movie.releaseDate || 
                           !movie.overview;

      if (needsMetadata && newPlanToWatchValue) {
        try {
          console.log(`🔍 [COMMIT-WATCH] Fetching complete metadata for ${movie.title}`);
          const fetchedMovie = await fetchMovieDetails(movie.tmdbId, undefined, user?.id);
          
          // Update with complete metadata
          completeMovie = {
            ...fetchedMovie,
            favorite: existingMovie?.favorite ?? movie.favorite ?? false,
            planToWatch: newPlanToWatchValue
          };
          
          // Update local state with complete metadata
          if (existingMovie) {
            updateMovieState(movie.tmdbId, completeMovie);
          } else {
            // Movie was already added, just update with complete data
            updateMovieState(movie.tmdbId, completeMovie);
          }
          
        } catch (error) {
          console.warn(`⚠️ [COMMIT-WATCH] Failed to fetch metadata, keeping basic data:`, error);
        }
      }

      // Persist to database (background operation)
      const result = newPlanToWatchValue 
        ? await SingleListService.addToList(completeMovie, SINGLE_LIST_TYPE.PLAN_TO_WATCH)
        : await SingleListService.removeFromList(movie.tmdbId, SINGLE_LIST_TYPE.PLAN_TO_WATCH);

      if (!result.success) {
        console.error(`❌ [COMMIT-WATCH] Database operation failed, but local state updated:`, result.error);
        // Local state is already updated, so UI remains responsive
      } else {
        console.log(`✅ [COMMIT-WATCH] Database operation successful for: ${movie.title}`);
      }

    } catch (error) {
      console.error(`❌ [COMMIT-WATCH] Background operation failed for ${movie.title}:`, error);
      // Local state is already updated, so UI remains functional
    }
  }, [allMovies, addMovie, updateMovieState, getVisualState, user?.id]);

  return { commitTogglePlanToWatch };
};
